import React, { FC, useEffect, useRef } from 'react';
import moment from 'moment';
import Empty from '../../empty';
import { echartsResize } from '@/utils';
import * as echarts from 'echarts/core';
import { LineChart } from 'echarts/charts';
import { renderToString } from 'react-dom/server';
import { CanvasRenderer } from 'echarts/renderers';
import { GridComponent, DataZoomComponent, TooltipComponent } from 'echarts/components';
import './index.less';

interface ChartType {
  chartTitle: string;
  seriesName: string;
  uint: string;
  data: any;
  dataKey: string;
  timeType: string;
}

echarts.use([GridComponent, CanvasRenderer, TooltipComponent, DataZoomComponent, LineChart]);

const GraphicComponent: FC<ChartType> = ({
  data,
  chartTitle,
  seriesName,
  uint,
  dataKey,
  timeType,
}) => {
  const chartRef = useRef<any>(null);

  // 初始化 echarts
  const initChart = () => {
    if (!chartRef.current) return;
    try {
      const xAxisData = data.map((v: any) => v.date);
      const chartInstance = echarts.init(chartRef.current);
      echartsResize('add', chartInstance);
      chartInstance.setOption({
        color: [getComputedStyle(document.documentElement).getPropertyValue('--superapp-primary-1') || '#2561F5'],
        grid: {
          top: 8,
          left: 0,
          right: 24,
          bottom: 16,
          containLabel: true,
        },
        tooltip: {
          trigger: 'axis',
          padding: [8, 12],
          backgroundColor: '#ffffff',
          extraCssText: 'box-shadow: 4px 0 16px 0 rgba(29,42,68,0.08);',
          className: 'chart-content-graphic-container-tooltip-box',
          formatter: (params: any[]) => {
            const DOM = (
              <div className="custom-tooltip-style">
                <div className="title">
                  {timeType === 'quarter'
                    ? `${moment(params[0].axisValueLabel).format('YYYY-MM-DD')} ~ ${moment(
                        params[0].axisValueLabel,
                      )
                        .add('6', 'days')
                        .format('YYYY-MM-DD')}`
                    : params[0].axisValueLabel}
                </div>
                {params.map((vv) => (
                  <div className="data-item" key={vv.axisValueLabel}>
                    <span className="data-item-icon" style={{ backgroundColor: vv.color }} />
                    <span className="data-item-value">
                      {vv.value || 0}&nbsp;
                      {uint || ''}
                    </span>
                  </div>
                ))}
              </div>
            );

            return renderToString(DOM);
          },
        },
        xAxis: {
          type: 'category',
          data: xAxisData,
          axisLabel: {
            color: '#B4BACC',
            lineHeight: 20,
            formatter: function (value, index) {
              if (timeType === 'year') { // 年度
                return moment(value).format('MMM');
              }
              if(timeType === 'quarter') { // 季度
                if (index === 0 || moment(value).format('MMM') !== moment(data[index - 1]?.date).format('MMM')) {
                  return moment(value).format('MMM');
                }
                return '';
              }
              return moment(value).format('YYYY-MM-DD');
            },
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: '#E6E8EB',
            },
          },
        },
        yAxis: {
          type: 'value',
          axisLine: {
            show: true,
            lineStyle: {
              color: '#E6E8EB',
            },
          },
          splitLine: {
            lineStyle: {
              color: '#E6E8EB',
            },
          },
          axisLabel: {
            color: '#B4BACC',
          },
        },
        dataZoom: [
          {
            type: 'inside',
            start: 0,
            end: 100,
          },
          {
            right: 24,
            bottom: 0,
            start: 0,
            end: 100,
            height: 8,
            showDetail: false,
            borderRadius: 2,
            moveHandleSize: 0,
            handleStyle: {
              color: getComputedStyle(document.documentElement).getPropertyValue('--superapp-primary-3') || '#7EA3FF',
              borderColor: getComputedStyle(document.documentElement).getPropertyValue('--superapp-primary-3') || '#7EA3FF',
            },
            backgroundColor: '#EBF0FF',
            borderColor: '#EBF0FF',
            dataBackground: {
              lineStyle: {
                color: '#EBF0FF',
              },
              areaStyle: {
                color: '#EBF0FF',
              },
            },
            fillerColor: '#D1DDFF',
            selectedDataBackground: {
              lineStyle: {
                color: '#EBF0FF',
              },
              areaStyle: {
                color: '#EBF0FF',
              },
            },
          },
        ],
        series: [
          {
            type: 'line',
            name: seriesName,
            data: data.map((v: any) => v[dataKey]),
          },
        ],
      });
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (!data?.length) return;
    initChart();
  }, [data]);

  return (
    <div className="chart-content">
      {data?.length ? (
        <>
          <div className="chart-title">
            <div className="title">{chartTitle}</div>
          </div>
          <div className="graphic-container" ref={chartRef} style={{ height: 280 }} />
        </>
      ) : (
        <Empty />
      )}
    </div>
  );
};

export default GraphicComponent;
